import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

// SignInModal — global overlay triggered by `openSignIn(reason)`.
// One-click Google login via Emergent-managed OAuth.
export default function SignInModal() {
  const { modal, closeSignIn, signIn } = useAuth();
  const { open, reason } = modal;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] bg-slate-900/60 backdrop-blur-sm grid place-items-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) closeSignIn(); }}
        >
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            data-testid="signin-modal"
            className="bg-white rounded-3xl w-full max-w-md p-8 shadow-2xl relative"
          >
            <button
              onClick={closeSignIn}
              className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-900"
              aria-label="Close"
              data-testid="signin-modal-close"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="eyebrow">§ Sign in</div>
            <h3 className="mt-3 font-display font-medium text-3xl tracking-tight text-slate-900 leading-[1.05]">
              Continue with <span className="italic font-light text-[#2563EB]">Google.</span>
            </h3>
            <p className="mt-3 text-[13.5px] text-slate-500 leading-relaxed">
              {reason || "Sign in to save coaches, message them privately, and book sessions."}
            </p>

            <button
              type="button"
              data-testid="signin-google-btn"
              onClick={() => signIn()}
              className="mt-6 w-full h-12 rounded-full bg-slate-900 hover:bg-[#2563EB] text-white text-[14px] font-semibold inline-flex items-center justify-center gap-3 transition-colors"
            >
              <GoogleGlyph />
              Continue with Google
            </button>

            <p className="mt-4 text-[11.5px] text-slate-400 leading-relaxed">
              By continuing you agree to our <a className="underline hover:text-slate-700" href="/terms">Terms</a> and <a className="underline hover:text-slate-700" href="/privacy">Privacy Policy</a>. We never post to Google on your behalf.
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function GoogleGlyph() {
  return (
    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-white">
      <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden>
        <path fill="#4285F4" d="M23.5 12.3c0-.8-.1-1.6-.2-2.4H12v4.5h6.5c-.3 1.5-1.1 2.7-2.4 3.6v3h3.9c2.3-2.1 3.5-5.2 3.5-8.7z"/>
        <path fill="#34A853" d="M12 24c3.2 0 5.9-1 7.9-2.9l-3.9-3c-1.1.8-2.5 1.2-4 1.2-3.1 0-5.7-2.1-6.6-4.9H1.4v3.1C3.4 21.4 7.4 24 12 24z"/>
        <path fill="#FBBC05" d="M5.4 14.4A7.2 7.2 0 0 1 5 12c0-.8.1-1.6.4-2.4V6.5H1.4A11.9 11.9 0 0 0 0 12c0 1.9.5 3.8 1.4 5.5l4-3.1z"/>
        <path fill="#EA4335" d="M12 4.8c1.8 0 3.4.6 4.6 1.8l3.4-3.4C17.9 1.2 15.2 0 12 0 7.4 0 3.4 2.6 1.4 6.5l4 3.1C6.3 6.9 8.9 4.8 12 4.8z"/>
      </svg>
    </span>
  );
}
